import { api } from './http';
import type {
  PortalAuditEntry,
  PortalDocumentRequest,
  PortalDocumentRequestInput,
  PortalQueueItem,
  PortalSharedDoc,
  PortalStatus,
  PortalUploadItem,
} from './types';

export interface SharePortalDocumentPayload {
  dropbox_path: string;
  display_name: string;
  document_type?: string;
  file_size_bytes?: number;
  expires_at?: string;
}

export const getPortalStatus = (matterId: number) =>
  api.get<PortalStatus>(`/matters/${matterId}/portal/status`).then(r => r.data);

export const sendPortalInvite = (matterId: number, contactId: number, email: string) =>
  api.post(`/matters/${matterId}/portal/invite`, { contact_id: contactId, email }).then(r => r.data);

export const sharePortalDocument = (matterId: number, data: SharePortalDocumentPayload) =>
  api.post<PortalSharedDoc>(`/matters/${matterId}/portal/share-document`, data).then(r => r.data);

export const getPortalSharedDocs = (matterId: number) =>
  api.get<PortalSharedDoc[]>(`/matters/${matterId}/portal/shared-documents`).then(r => r.data);

export const revokePortalSharedDoc = (matterId: number, docId: number) =>
  api.delete(`/matters/${matterId}/portal/shared-documents/${docId}`).then(r => r.data);

export const getPortalUploads = (matterId: number) =>
  api.get<PortalUploadItem[]>(`/matters/${matterId}/portal/uploads`).then(r => r.data);

export const getPortalAuditLog = (matterId: number, limit = 50) =>
  api.get<PortalAuditEntry[]>(`/matters/${matterId}/portal/audit-log`, { params: { limit } }).then(r => r.data);

export const revokePortalAccess = (matterId: number) =>
  api.post(`/matters/${matterId}/portal/revoke-access`).then(r => r.data);

export const getPortalQueue = () =>
  api.get<PortalQueueItem[]>('/portal-queue').then(r => r.data);

export const getPortalQueueCount = () =>
  api.get<{ count: number }>('/portal-queue/count').then(r => r.data);

export const getPortalDocViewLink = (docId: number, signal?: AbortSignal) =>
  api.get<{ url: string }>(`/portal-queue/${docId}/view-link`, { signal }).then(r => r.data);

export const getPortalDocContent = (docId: number, signal?: AbortSignal): Promise<ArrayBuffer> =>
  api.get(`/portal-queue/${docId}/content`, { responseType: 'arraybuffer', signal }).then(r => r.data);

export const approvePortalDocument = (matterId: number, docId: number) =>
  api.post(`/matters/${matterId}/portal/approve-document/${docId}`).then(r => r.data);

export const rejectPortalDocument = (matterId: number, docId: number, reason?: string) =>
  api.post(`/matters/${matterId}/portal/reject-document/${docId}`, { reason }).then(r => r.data);

export const getDocumentRequests = (matterId: number) =>
  api.get<PortalDocumentRequest[]>(`/matters/${matterId}/portal/document-requests`).then(r => r.data);

export const createDocumentRequest = (matterId: number, data: PortalDocumentRequestInput) =>
  api.post<PortalDocumentRequest>(`/matters/${matterId}/portal/document-requests`, data).then(r => r.data);

export const updateDocumentRequest = (matterId: number, requestId: number, data: Partial<PortalDocumentRequest>) =>
  api.patch<PortalDocumentRequest>(`/matters/${matterId}/portal/document-requests/${requestId}`, data).then(r => r.data);

export const deleteDocumentRequest = (matterId: number, requestId: number) =>
  api.delete(`/matters/${matterId}/portal/document-requests/${requestId}`).then(r => r.data);
